import { useApolloClient } from "@apollo/client";
import flagsmith from "flagsmith";
import { useCallback } from "react";
import { fetchOptimizedBoundaryMap } from "src/components/Boundary/fetchOptimizedBoundaryMap";
import { GET_BOUNDARIES_BY_FORM } from "src/components/Boundary/queries";
import { addressLookup, fromGQL } from "src/components/Boundary/services";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useGlossary } from "src/hooks/useGlossary";
import { useLoadGooglePlacesScript } from "src/hooks/useLoadGoogleMaps";
import { RankedSchoolsRemovalConfirmationDialogBody } from "src/scenes/parent/forms/components/Dialogs/RankedSchoolsRemovalConfirmationDialogBody";
import { FormAddress } from "src/schemas/Address";
import { useEligibilityService } from "src/services/eligibility/useEligibilityService";
import { calculateSchoolsToRemoveByLocation } from "src/services/formTemplate/preRankingSection";
import { isLocationBoundariesFilter } from "src/services/formTemplateFilters";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { GET_GRADE_CONFIG_ANSWER } from "../graphql/queries";

type Props = { formTemplateId: uuid; formId: uuid; rankingEnabled: boolean };

export function useUpdateFormAddress({
  formTemplateId,
  formId,
  rankingEnabled,
}: Props) {
  const { glossary } = useGlossary();
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Edit address?",
    body: null,
    cancelButton: { label: "No, cancel" },
    confirmButton: { label: "Yes, continue" },
  });
  const apolloClient = useApolloClient();

  useLoadGooglePlacesScript();

  const calculateSchoolsToRemove = useCallback(
    async function (
      rankedSchools: GQL.GetSchoolsRank_form_school_rank[],
      completeQuestions: readonly AF.Question<AF.WithId>[],
      question: AF.Address<AF.WithId>,
      answer?: FormAddress
    ): Promise<GQL.GetSchoolsRank_form_school_rank[]> {
      if (rankedSchools.length === 0) {
        return [];
      }

      if (!question.filters?.some(isLocationBoundariesFilter)) {
        return [];
      }

      const gradeConfigQuery = apolloClient.query<
        GQL.GetGradeConfigAnswer,
        GQL.GetGradeConfigAnswerVariables
      >({
        query: GET_GRADE_CONFIG_ANSWER,
        variables: {
          form_id: formId,
        },
      });

      const boundariesQuery = flagsmith.hasFeature("boundary-management")
        ? fetchOptimizedBoundaryMap(formId, true)
        : apolloClient
            .query<GQL.GetBoundariesByForm, GQL.GetBoundariesByFormVariables>({
              query: GET_BOUNDARIES_BY_FORM,
              variables: {
                form_id: formId,
              },
            })
            .then((result) => fromGQL(result.data));

      const [gradeConfig, boundaries] = await Promise.all([
        gradeConfigQuery,
        boundariesQuery,
      ]);

      if (!window.google?.maps) {
        throw new Error("Unable to load google maps");
      }

      let location = undefined;
      try {
        location = answer
          ? answer.geom?.coordinates ??
            (await addressLookup(new google.maps.Geocoder(), answer))
          : undefined;
      } catch (e: unknown) {
        console.log(
          `unable to gecode address: ${
            e instanceof Error ? e.message : "Unknown"
          }`
        );
      }

      const schoolIdsToRemove = calculateSchoolsToRemoveByLocation(
        rankedSchools.flatMap((rankedSchool) => rankedSchool.school),
        completeQuestions,
        gradeConfig.data.grades_answer[0]?.grade_config_id ?? undefined,
        boundaries,
        location
      ).map((school) => school.id);

      if (schoolIdsToRemove.length === 0) return [];

      const rankedSchoolsToRemove = rankedSchools.filter((rankedSchool) =>
        schoolIdsToRemove.includes(rankedSchool.school.id)
      );

      return rankedSchoolsToRemove;
    },
    [formId, apolloClient]
  );

  const confirmEligibilityQuestionUpdate = useCallback(
    async function (
      rankedSchoolsToRemove: GQL.GetSchoolsRank_form_school_rank[]
    ): Promise<boolean> {
      if (rankedSchoolsToRemove.length === 0) {
        return true;
      }

      const isConfirmed = await confirm({
        body: (
          <RankedSchoolsRemovalConfirmationDialogBody
            schoolsToRemove={rankedSchoolsToRemove}
            body={glossary`Editing your address will impact your eligibility and remove these schools from your ranking:`}
            confirmation="Are you sure you want to edit your address?"
            rankingEnabled={rankingEnabled}
          />
        ),
      });
      return isConfirmed;
    },
    [confirm, glossary, rankingEnabled]
  );

  const eligibilityService = useEligibilityService({ formTemplateId });
  if (eligibilityService.enabled) {
    return {
      confirm: () => true,
      confirmationDialog: null,
      calculateSchoolsToRemove: () => [],
    };
  }

  return {
    confirm: confirmEligibilityQuestionUpdate,
    confirmationDialog,
    calculateSchoolsToRemove,
  };
}
